import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Loader2, Mail, MailOpen, Reply } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrganizerMessages() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [messages, setMessages] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/organizations/me/messages`, { headers });
      setMessages(r.data.messages || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load messages'); setMessages([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const markRead = async (m) => {
    if (m.status === 'read') return;
    try {
      await axios.post(`${API}/organizations/me/messages/${m.message_id}/read`, {}, { headers });
      setMessages((cur) => cur.map((x) => x.message_id === m.message_id ? { ...x, status: 'read' } : x));
    } catch { /* non-fatal */ }
  };

  if (messages === null) return <div className="min-h-screen bg-background pt-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-background pt-20 pb-20" data-testid="organizer-messages-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-4">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Mail className="h-6 w-6" /> Messages</h1>
        <p className="text-sm text-muted-foreground">Messages people send from your public organizer profile land here (and in your email).</p>
        {messages.length === 0 && (
          <Card className="border-border"><CardContent className="p-8 text-center text-muted-foreground">No messages yet.</CardContent></Card>
        )}
        {messages.map((m) => (
          <Card key={m.message_id}
            className={`border ${m.status === 'read' ? 'border-border' : 'border-fuchsia-500/50 bg-fuchsia-500/5'}`}
            onClick={() => markRead(m)} data-testid={`org-message-${m.message_id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-semibold flex items-center gap-2">
                    {m.status === 'read' ? <MailOpen className="h-4 w-4 text-muted-foreground" /> : <Mail className="h-4 w-4 text-fuchsia-400" />}
                    {m.from_name}
                    {m.status !== 'read' && <Badge className="bg-fuchsia-600 text-white border-0 text-[10px]">NEW</Badge>}
                  </p>
                  <p className="text-xs text-muted-foreground">{m.from_email}{m.organization_name ? ` · to ${m.organization_name}` : ''} · {(m.created_at || '').slice(0, 10)}</p>
                </div>
                <Button size="sm" variant="outline" asChild data-testid={`org-message-reply-${m.message_id}`}>
                  <a href={`mailto:${m.from_email}?subject=Re: your message on Eventism`}><Reply className="h-3.5 w-3.5 mr-1" /> Reply</a>
                </Button>
              </div>
              <p className="text-sm whitespace-pre-line border-l-2 border-border pl-3">{m.message}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
